import { useAuth } from '@clerk/clerk-react'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createApiClient, type ApiClient } from './api'

const ApiContext = createContext<ApiClient | null>(null)

/**
 * One client per session, holding Clerk's `getToken` rather than a token: tokens are short-lived
 * and Clerk refreshes them, so every request asks for the current one.
 */
export function ApiProvider({ baseUrl, children }: { baseUrl: string; children: ReactNode }) {
  const { getToken } = useAuth()
  const client = useMemo(
    () => createApiClient({ baseUrl, getToken: () => getToken() }),
    [baseUrl, getToken],
  )

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>
}

/** Test seam: screens can be rendered against a stub client without Clerk. */
export function ApiClientProvider({
  client,
  children,
}: {
  client: ApiClient
  children: ReactNode
}) {
  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>
}

export function useApi(): ApiClient {
  const client = useContext(ApiContext)
  if (!client) throw new Error('useApi must be used inside an ApiProvider')
  return client
}
